import React, { useState, useEffect, useContext } from "react";
import { ArrowRight, Clock, Users, Star, BookOpen, X } from "lucide-react";
import "./../../../assets/css/student/Courses/courses.css";
import AppContext from "../../../context/Appcontext";

// ─── Course Data ──────────────────────────────────────────────────────────────
const coursesData = [
  {
    id: 1,
    title: "Web Development",
    description: "Master the art of building modern websites from scratch",
    mentor: "Sir Imran & Team",
    students: 450,
    duration: "6 months",
    rating: 4.8,
    price: "35000",
    category: "Development",
    skills: [
      {
        name: "HTML5",
        icon: "📄",
        color: "#e34c26",
        explanation:
          "HTML5 is the backbone of web pages. It's like the skeleton of a house - it gives structure to everything you see on a website. You'll learn how to create headings, paragraphs, links, images, forms, and organize content properly. Think of it as learning the alphabet before writing stories!",
      },
      {
        name: "CSS3",
        icon: "🎨",
        color: "#264de4",
        explanation:
          "CSS3 is what makes websites beautiful! If HTML is the skeleton, CSS is the clothing and makeup. You'll learn how to add colors, fonts, layouts, animations, and make your websites responsive so they look great on phones, tablets, and computers. It's like being a digital artist!",
      },
      {
        name: "JavaScript",
        icon: "⚡",
        color: "#f7df1e",
        explanation:
          "JavaScript brings websites to life! It's the brain that makes things interactive. Want a button that changes color when clicked? A form that validates data? A slideshow that moves automatically? That's all JavaScript! You'll learn how to make websites smart and interactive.",
      },
      {
        name: "React",
        icon: "⚛️",
        color: "#61dafb",
        explanation:
          "React is a powerful tool that helps you build complex websites more easily. Imagine building with LEGO blocks instead of carving everything from scratch - that's React! You'll learn to create reusable components, manage data efficiently, and build modern single-page applications like Netflix or Facebook.",
      },
    ],
  },
  {
  id: 2,
  title: "Data Structures & Algorithms",
  description: "Master DSA concepts with hands-on problem solving and coding practice",
  mentor: "Mam Mahnoor",
  students: 320,
  duration: "4 months",
  rating: 4.9,
  price: "25000",
  category: "Programming",
  skills: [
    {
      name: "Arrays & Strings",
      icon: "📋",
      color: "#3776ab",
      explanation:
        "Arrays are the foundation of DSA! You'll learn how to store and access data in a sequence, manipulate strings, and solve classic problems like reversing an array or finding duplicates. Think of an array like a row of lockers - each has a number and stores one item!",
    },
    {
      name: "Linked Lists",
      icon: "🔗",
      color: "#ff6b6b",
      explanation:
        "A linked list is like a chain - each link (node) holds data and points to the next one. You'll learn singly and doubly linked lists, how to insert, delete, and traverse nodes. Great for understanding how memory and pointers work in real programs!",
    },
    {
      name: "Stacks & Queues",
      icon: "📚",
      color: "#4ecdc4",
      explanation:
        "Stack works like a pile of plates - last in, first out (LIFO). Queue works like a line at a shop - first in, first out (FIFO). You'll use these to solve problems like balancing brackets, undo/redo operations, and scheduling tasks in programs!",
    },
    {
      name: "Trees & Graphs",
      icon: "🌳",
      color: "#95e1d3",
      explanation:
        "Trees and graphs are used everywhere - from file systems to Google Maps! You'll learn binary trees, BST, BFS, and DFS traversal. Graphs help model real-world networks like social media connections or shortest path finding. The most powerful structures in DSA!",
    },
  ],
},
  {
    id: 3,
    title: "UI/UX Design",
    description: "Create beautiful and user-friendly digital experiences",
    mentor: "Mam Javeria",
    students: 280,
    duration: "3 months",
    rating: 4.7,
    price: "20000",
    category: "Design",
    skills: [
      {
        name: "Figma",
        icon: "🎨",
        color: "#f24e1e",
        explanation:
          "Figma is your digital canvas for designing websites and apps. Before writing code, designers create mockups in Figma. You'll learn to design interfaces, create prototypes, work with colors and typography, and bring your creative ideas to life. It's like sketching before painting!",
      },
      {
        name: "User Research",
        icon: "🔍",
        color: "#a259ff",
        explanation:
          "User Research is about understanding people - what they need, what frustrates them, what they love. You'll learn to conduct interviews, surveys, and tests to gather insights. Good design starts with understanding your users, not just making things look pretty!",
      },
      {
        name: "Wireframing",
        icon: "📐",
        color: "#0acf83",
        explanation:
          "Wireframing is creating a basic blueprint of your design - like an architect's sketch. It's simple, black and white, showing where buttons, text, and images go. This helps you plan the structure before adding colors and details. Think of it as your design's skeleton!",
      },
      {
        name: "Prototyping",
        icon: "🔗",
        color: "#ff7262",
        explanation:
          "Prototyping is making your designs interactive! You connect screens together so clicking a button takes you to the next page. This lets you test your design before developers build it. It's like creating a playable demo of your vision - people can click through and experience it!",
      },
    ],
  },
  {
    id: 4,
    title: "App Development",
    description: "Build beautiful mobile apps for Android and iOS from scratch",
    mentor: "Sir Ahmed",
    students: 195,
    duration: "5 months",
    rating: 4.9,
    price: "40000",
    category: "Mobile Dev",
    skills: [
      {
        name: "Flutter Basics",
        icon: "📱",
        color: "#54c5f8",
        explanation:
          "Flutter is Google's powerful toolkit for building mobile apps! You'll learn widgets, layouts, and how to design beautiful screens that work on both Android and iOS. Think of widgets like LEGO blocks - you combine them to build any UI you can imagine!",
      },
      {
        name: "Dart Language",
        icon: "🎯",
        color: "#00b4ab",
        explanation:
          "Dart is the programming language used with Flutter. It's clean, fast, and easy to learn! You'll master variables, functions, classes, and async programming. Dart is the engine that powers your Flutter app - without it, your app won't move!",
      },
      {
        name: "State Management",
        icon: "⚙️",
        color: "#786fa6",
        explanation:
          "State management controls how your app handles data changes. When a user logs in or adds to cart, the app needs to update instantly. You'll learn Provider and GetX to manage app state efficiently - making your apps smooth, fast, and bug-free!",
      },
      {
        name: "Firebase Integration",
        icon: "🔥",
        color: "#ff6b6b",
        explanation:
          "Firebase is Google's backend platform that gives your app superpowers! Authentication, real-time database, cloud storage - all without building a backend from scratch. You'll connect your Flutter app to Firebase and make it a fully working real-world application!",
      },
    ],
  },
  {
    id: 5,
    title: "Dart Programming",
    description: "Master Dart language - the backbone of modern app development",
    mentor: "Sir Ahmed",
    students: 160,
    duration: "2 months",
    rating: 4.8,
    price: "18000",
    category: "Mobile Dev",
    skills: [
      {
        name: "Dart Fundamentals",
        icon: "📝",
        color: "#54c5f8",
        explanation:
          "Start from zero and build a strong Dart foundation! Variables, data types, loops, conditions, and functions - you'll master them all. Dart is modern, fast, and beginner-friendly. Once you know Dart well, picking up Flutter becomes 10x easier!",
      },
      {
        name: "OOP in Dart",
        icon: "🧱",
        color: "#00b4ab",
        explanation:
          "Object-Oriented Programming in Dart makes your code clean and reusable. You'll learn classes, objects, inheritance, and polymorphism. Think of OOP like designing a car blueprint - once made, you can create unlimited cars from it without rewriting everything!",
      },
      {
        name: "Async Programming",
        icon: "⚡",
        color: "#f8b500",
        explanation:
          "Async programming lets your app do multiple things at once without freezing. Fetching data from internet, reading files, waiting for user input - all handled smoothly with Future, async, and await in Dart. Makes your app feel fast and responsive!",
      },
      {
        name: "Collections & Generics",
        icon: "📦",
        color: "#786fa6",
        explanation:
          "Collections like Lists, Maps, and Sets help you manage groups of data efficiently. Generics make your code type-safe and reusable. You'll write smarter, cleaner code that handles real-world data like user lists, product catalogs, and more with ease!",
      },
    ],
  },
  {
    id: 6,
    title: "Backend Development",
    description: "Build powerful server-side applications and REST APIs from scratch",
    mentor: "Sir Bilal",
    students: 210,
    duration: "4 months",
    rating: 4.8,
    price: "35000",
    category: "Backend",
    skills: [
      {
        name: "Node.js",
        icon: "🟩",
        color: "#3c873a",
        explanation:
          "Node.js lets you run JavaScript on the server side! You'll learn how to build fast, scalable backend applications. Think of Node.js as the engine of your server - it handles thousands of requests at once without slowing down. Perfect for building real-time apps and APIs!",
      },
      {
        name: "Express.js",
        icon: "🚀",
        color: "#ff6b6b",
        explanation:
          "Express.js is the most popular Node.js framework that makes building APIs super easy! You'll create routes, handle requests, add middlewares, and structure your backend like a pro. It's like a highway system - Express manages all the traffic coming to your server smoothly!",
      },
      {
        name: "Databases",
        icon: "🗄️",
        color: "#f8b500",
        explanation:
          "Every app needs to store data - that's where databases come in! You'll learn both MongoDB (NoSQL) and MySQL (SQL). MongoDB stores data like JSON objects, great for flexible data. MySQL stores data in tables like Excel. You'll know when and how to use both like a pro!",
      },
      {
        name: "REST APIs",
        icon: "🔌",
        color: "#786fa6",
        explanation:
          "REST APIs are the bridge between frontend and backend. When you open Instagram and see posts - that's a REST API fetching data! You'll build complete CRUD APIs (Create, Read, Update, Delete), handle authentication with JWT tokens, and connect your backend to any frontend app!",
      },
    ],
  },
];

// ─── SkeletonCourseCard ───────────────────────────────────────────────────────
const SkeletonCourseCard = () => {
  return (
    <div className="modern-course-card skeleton-card-wrapper">
      <div className="sk-card-top">
        <div
          className="skeleton skeleton-pill"
          style={{ height: "24px", width: "90px" }}
        />
        <div className="skeleton" style={{ height: "16px", width: "45px" }} />
      </div>
      <div
        className="skeleton"
        style={{ height: "24px", width: "70%", marginTop: "12px" }}
      />
      <div className="skeleton-block" style={{ marginTop: "8px" }}>
        <div className="skeleton skeleton-text" />
        <div className="skeleton skeleton-text" style={{ width: "75%" }} />
      </div>
      <div className="sk-topics-grid" style={{ marginTop: "14px" }}>
        <div className="skeleton sk-topic-item" />
        <div className="skeleton sk-topic-item" />
        <div className="skeleton sk-topic-item" />
        <div className="skeleton sk-topic-item" />
      </div>
      <div className="sk-meta-row" style={{ marginTop: "14px" }}>
        <div className="sk-meta-item">
          <div
            className="skeleton skeleton-circle"
            style={{ width: "16px", height: "16px" }}
          />
          <div className="skeleton" style={{ height: "10px", width: "80px" }} />
        </div>
        <div className="sk-meta-item">
          <div
            className="skeleton skeleton-circle"
            style={{ width: "16px", height: "16px" }}
          />
          <div className="skeleton" style={{ height: "10px", width: "65px" }} />
        </div>
      </div>
      <div className="sk-card-footer" style={{ marginTop: "14px" }}>
        <div className="skeleton" style={{ height: "26px", width: "90px" }} />
        <div
          className="skeleton skeleton-pill"
          style={{ height: "38px", width: "130px" }}
        />
      </div>
      <div
        className="skeleton"
        style={{ height: "10px", width: "110px", marginTop: "12px" }}
      />
    </div>
  );
};

// ─── CourseCard ───────────────────────────────────────────────────────────────
const CourseCard = ({ course, onViewDetails }) => {
  return (
    <div className="modern-course-card">
      <div className="course-card-header">
        <div className="course-header-top">
          <span className="course-category-badge">{course.category}</span>
          <div className="course-rating">
            <Star size={16} fill="#fbbf24" color="#fbbf24" />
            <span>{course.rating}</span>
          </div>
        </div>
        <h2 className="course-card-title">{course.title}</h2>
        <p className="course-card-description">{course.description}</p>
      </div>
      <div className="course-skills-grid">
        {course.skills.map((skill, index) => (
          <div
            key={index}
            className="skill-badge-modern"
            style={{ "--skill-color": skill.color }}
          >
            <span className="skill-badge-icon">{skill.icon}</span>
            <span className="skill-badge-name">{skill.name}</span>
          </div>
        ))}
      </div>
      <div className="course-card-footer">
        <div className="course-meta-info">
          <div className="meta-item">
            <Users size={16} />
            <span>{course.students} students</span>
          </div>
          <div className="meta-item">
            <Clock size={16} />
            <span>{course.duration}</span>
          </div>
        </div>
        <div className="course-action-row">
          <div className="course-price">Rs. {course.price}</div>
          <button
            className="view-details-btn"
            onClick={() => onViewDetails(course)}
          >
            View Details
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
      <div className="course-mentor-tag">
        <BookOpen size={14} />
        <span>By {course.mentor}</span>
      </div>
    </div>
  );
};

// ─── CourseDetailPage ─────────────────────────────────────────────────────────
const CourseDetailPage = ({ course, onBack, onEnroll }) => {
  return (
    <div className="course-detail-page">
      <button className="back-button" onClick={onBack}>
        ← Back to Courses
      </button>
      <div className="detail-hero">
        <div className="detail-hero-content">
          <span className="detail-category">{course.category}</span>
          <h1 className="detail-title">{course.title}</h1>
          <p className="detail-description">{course.description}</p>
          <div className="detail-meta-row">
            <div className="detail-meta-item">
              <Star size={20} fill="#fbbf24" color="#fbbf24" />
              <span>{course.rating} Rating</span>
            </div>
            <div className="detail-meta-item">
              <Users size={20} />
              <span>{course.students} Students</span>
            </div>
            <div className="detail-meta-item">
              <Clock size={20} />
              <span>{course.duration}</span>
            </div>
            <div className="detail-meta-item">
              <BookOpen size={20} />
              <span>{course.mentor}</span>
            </div>
          </div>
          <div className="detail-price-section">
            <div className="detail-price">Rs. {course.price}</div>
            <button className="enroll-now-btn" onClick={() => onEnroll(course)}>
              Enroll Now
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </div>
      <div className="skills-detail-section">
        <h2 className="skills-section-title">What You'll Learn</h2>
        <div className="skills-detail-grid">
          {course.skills.map((skill, index) => (
            <div
              key={index}
              className="skill-detail-card"
              style={{ "--skill-color": skill.color }}
            >
              <div className="skill-detail-header">
                <div className="skill-detail-icon">{skill.icon}</div>
                <h3 className="skill-detail-name">{skill.name}</h3>
              </div>
              <p className="skill-detail-explanation">{skill.explanation}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── EnrollmentModal ──────────────────────────────────────────────────────────
const EnrollmentModal = ({ course, onClose }) => {
  const { regExpChecker } = useContext(AppContext); // ✅

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    education: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // ========== VALIDATION FUNCTION ==========
  const handleValidation = (field, value) => {
    const error = regExpChecker(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  // ========== SUBMIT FUNCTION ==========
  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMessage("");

    // ✅ Submit pe sab fields validate karo
    const fields = {
      fullName: formData.fullName,
      enrollEmail: formData.email,
      phone: formData.phone,
      education: formData.education,
      message: formData.message,
    };

    const newErrors = {};
    Object.entries(fields).forEach(([field, value]) => {
      const error = regExpChecker(field, value);
      if (error) newErrors[field] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      setTimeout(() => onClose(), 2000);
    }, 1500);
  };

  return (
    <div className="enrollment-modal-overlay" onClick={onClose}>
      <div className="enrollment-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>
          <X size={24} />
        </button>

        {!isSuccess ? (
          <>
            <div className="modal-header">
              <h2 className="modal-title">Enroll in {course.title}</h2>
              <p className="modal-subtitle">
                Fill out the form below to start your learning journey
              </p>
            </div>

            <div className="modal-course-info">
              <div className="modal-info-item">
                <span className="info-label">Price:</span>
                <span className="info-value">Rs. {course.price}</span>
              </div>
              <div className="modal-info-item">
                <span className="info-label">Duration:</span>
                <span className="info-value">{course.duration}</span>
              </div>
              <div className="modal-info-item">
                <span className="info-label">Mentor:</span>
                <span className="info-value">{course.mentor}</span>
              </div>
            </div>

            <form className="enrollment-form" onSubmit={handleSubmit}>
              {/* ========== FULL NAME ========== */}
              <div className="form-group">
                <label htmlFor="fullName">Full Name *</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={(e) => {
                    setFormData({ ...formData, fullName: e.target.value });
                    handleValidation("fullName", e.target.value);
                  }}
                  placeholder="Enter your full name"
                />
                {errors.fullName && (
                  <small className="text-danger">{errors.fullName}</small>
                )}
              </div>

              {/* ========== EMAIL ========== */}
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData({ ...formData, email: e.target.value });
                    handleValidation("enrollEmail", e.target.value);
                  }}
                  placeholder="your.email@example.com"
                />
                {errors.enrollEmail && (
                  <small className="text-danger">{errors.enrollEmail}</small>
                )}
              </div>

              {/* ========== PHONE ========== */}
              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => {
                    setFormData({ ...formData, phone: e.target.value });
                    handleValidation("phone", e.target.value);
                  }}
                  placeholder="+92 300 1234567"
                />
                {errors.phone && (
                  <small className="text-danger">{errors.phone}</small>
                )}
              </div>

              {/* ========== EDUCATION ========== */}
              <div className="form-group">
                <label htmlFor="education">Current Education Level *</label>
                <select
                  id="education"
                  name="education"
                  value={formData.education}
                  onChange={(e) => {
                    setFormData({ ...formData, education: e.target.value });
                    handleValidation("education", e.target.value);
                  }}
                >
                  <option value="">Select your education level</option>
                  <option value="high-school">High School</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="undergraduate">Undergraduate</option>
                  <option value="graduate">Graduate</option>
                  <option value="other">Other</option>
                </select>
                {errors.education && (
                  <small className="text-danger">{errors.education}</small>
                )}
              </div>

              {/* ========== MESSAGE ========== */}
              <div className="form-group">
                <label htmlFor="message">
                  Why do you want to join this course? (Optional)
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={(e) => {
                    setFormData({ ...formData, message: e.target.value });
                    handleValidation("message", e.target.value);
                  }}
                  placeholder="Tell us about your goals and motivation..."
                  rows="4"
                ></textarea>
                {errors.message && (
                  <small className="text-danger">{errors.message}</small>
                )}
              </div>

              {/* ========== ERROR MESSAGE ========== */}
              {errorMessage && (
                <div
                  style={{
                    color: "red",
                    marginBottom: "10px",
                    fontSize: "14px",
                  }}
                >
                  ⚠️ {errorMessage}
                </div>
              )}

              <button
                type="submit"
                className="submit-enrollment-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Complete Enrollment"}
                {!isSubmitting && <ArrowRight size={20} />}
              </button>
            </form>
          </>
        ) : (
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h3>Enrollment Successful!</h3>
            <p>
              Thank you for enrolling in {course.title}. We'll contact you
              shortly with further details.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Courses (Main) ───────────────────────────────────────────────────────────
const Courses = () => {
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [enrollingCourse, setEnrollingCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  const filteredCourses = coursesData.filter(
    (course) =>
      course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.skills.some((skill) =>
        skill.name.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
  );

  const handleEnroll = (course) => {
    setEnrollingCourse(course);
    setShowEnrollModal(true);
  };

  if (selectedCourse) {
    return (
      <>
        <CourseDetailPage
          course={selectedCourse}
          onBack={() => setSelectedCourse(null)}
          onEnroll={handleEnroll}
        />
        {showEnrollModal && enrollingCourse && (
          <EnrollmentModal
            course={enrollingCourse}
            onClose={() => setShowEnrollModal(false)}
          />
        )}
      </>
    );
  }

  return (
    <div className="courses-page">
      {/* ── Hero Section ── */}
      <section className="courses-hero-section-new">
        <div className="courses-hero-left">
          {loading ? (
            <>
              <div
                className="skeleton"
                style={{ height: "42px", width: "85%", marginBottom: "12px" }}
              />
              <div
                className="skeleton"
                style={{ height: "42px", width: "70%", marginBottom: "20px" }}
              />
              <div
                className="skeleton skeleton-text"
                style={{ width: "90%", marginBottom: "6px" }}
              />
              <div
                className="skeleton skeleton-text"
                style={{ width: "65%", marginBottom: "24px" }}
              />
              <div className="sk-meta-row" style={{ marginBottom: "20px" }}>
                <div
                  className="skeleton skeleton-pill"
                  style={{ height: "48px", width: "260px" }}
                />
                <div
                  className="skeleton skeleton-pill"
                  style={{ height: "48px", width: "130px" }}
                />
              </div>
              <div className="sk-meta-row">
                <div
                  className="skeleton"
                  style={{ height: "12px", width: "130px" }}
                />
                <div
                  className="skeleton"
                  style={{ height: "12px", width: "170px" }}
                />
              </div>
            </>
          ) : (
            <>
              <h1 className="courses-hero-title">
                Upgrade Your Skills Up To{" "}
                <span className="courses-highlight">Superhero</span> Level
              </h1>
              <p className="courses-hero-subtitle">
                Don't let your strength be defeated, Explore the hidden skills
                within you!
              </p>
              <div className="courses-search-wrapper">
                <input
                  type="text"
                  placeholder="What skills are you looking for?"
                  className="courses-search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button className="courses-explore-btn">Explore Now</button>
              </div>
              <div className="courses-features">
                <div className="courses-feature-item">
                  <span className="feature-icon">✓</span>
                  <span>Monthly subscription</span>
                </div>
                <div className="courses-feature-item">
                  <span className="feature-icon">✓</span>
                  <span>30 day money back guarantee</span>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="courses-hero-right">
          {loading ? (
            <div
              className="skeleton skeleton-circle"
              style={{ width: "340px", height: "340px" }}
            />
          ) : (
            <div className="courses-illustration-wrapper">
              <div className="courses-circle-bg"></div>
              <div className="superhero-character">
                <div className="superhero-head">
                  <div className="superhero-hair"></div>
                  <div className="superhero-face">
                    <div className="superhero-eye left"></div>
                    <div className="superhero-eye right"></div>
                    <div className="superhero-smile"></div>
                  </div>
                </div>
                <div className="superhero-body">
                  <div className="superhero-cape"></div>
                  <div className="superhero-shirt"></div>
                  <div className="superhero-tie"></div>
                  <div className="superhero-arm left"></div>
                  <div className="superhero-arm right"></div>
                </div>
              </div>
              <div className="skill-badge badge-1">
                <span className="skill-icon">UX</span>
              </div>
              <div className="skill-badge badge-2">
                <span className="skill-icon">AI</span>
              </div>
              <div className="skill-badge badge-3">
                <span className="skill-icon">💻</span>
              </div>
              <div className="skill-badge badge-4">
                <span className="skill-icon">UI</span>
              </div>
              <div className="skill-badge badge-5">
                <span className="skill-icon">💡</span>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── Courses Section ── */}
      <div className="courses-container-new">
        <div className="courses-header">
          {loading ? (
            <>
              <div
                className="skeleton"
                style={{
                  height: "36px",
                  width: "280px",
                  margin: "0 auto 12px",
                }}
              />
              <div
                className="skeleton"
                style={{ height: "14px", width: "420px", margin: "0 auto" }}
              />
            </>
          ) : (
            <>
              <h1 className="courses-main-title">Explore Our Courses</h1>
              <p className="courses-subtitle">
                Choose from our comprehensive programs designed to level up your
                skills
              </p>
            </>
          )}
        </div>

        <div className="courses-grid">
          {loading
            ? coursesData.map((_, i) => <SkeletonCourseCard key={i} />)
            : filteredCourses.map((course) => (
                <CourseCard
                  key={course.id}
                  course={course}
                  onViewDetails={setSelectedCourse}
                />
              ))}
        </div>

        {!loading && filteredCourses.length === 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "60px 20px",
              color: "#64748b",
            }}
          >
            <h3>No courses found matching your search</h3>
          </div>
        )}
      </div>

      {/* ── Enrollment Modal ── */}
      {showEnrollModal && enrollingCourse && (
        <EnrollmentModal
          course={enrollingCourse}
          onClose={() => setShowEnrollModal(false)}
        />
      )}
    </div>
  );
};

export default Courses;
